import { useContext } from "react";

import PeopleIcon from "@mui/icons-material/People";
import Avatar from "react-avatar";
import type { DefaultGenerics } from "stream-chat";
import {
  ChannelPreviewUIComponentProps,
  useChatContext,
} from "stream-chat-react";
import { makeStyles } from "tss-react/mui";

import { BadgeCountsContext, UserContext } from "../../../../helpers";
import { useBadgePulse } from "../../hooks/useBadgePulse";
import { badgePulseStyles } from "../../styles/badgePulseStyles";
import "./TeamChannelPreview.css";

const useStyles = makeStyles()((theme) => ({
  unreadBadge: {
    backgroundColor: theme.palette.error.dark,
    color: theme.palette.primary.contrastText,
  },
  ...badgePulseStyles,
}));

export const TeamChannelPreview = ({
  channel,
  activeChannel,
  displayTitle,
  unread,
}: ChannelPreviewUIComponentProps<DefaultGenerics>) => {
  const { user } = useContext(UserContext);
  const { refreshStreamUnreadCounts } = useContext(BadgeCountsContext);
  const { setActiveChannel } = useChatContext();
  const { classes, cx } = useStyles();

  const isSelected = channel?.id === activeChannel?.id;
  const isManagersChannel =
    channel?.id === `company-${user.company.id}-client-${user.id}`;
  const readState = channel?.state?.read?.[user.id?.toString()];
  const stateUnreadMessages =
    typeof readState?.unread_messages === "number"
      ? readState.unread_messages
      : undefined;
  const unreadCountRaw =
    unread ??
    stateUnreadMessages ??
    channel?.state?.unreadCount ??
    (typeof channel?.countUnread === "function"
      ? channel.countUnread()
      : undefined) ??
    0;
  const unreadCount = Number(unreadCountRaw) || 0;
  const unreadCountDisplay = unreadCount > 99 ? "99+" : `${unreadCount}`;
  const pulseUnread = useBadgePulse(unreadCount);

  const handleSelectChannel = () => {
    setActiveChannel(channel);
    if (typeof channel?.markRead === "function") {
      Promise.resolve(channel.markRead()).finally(() => {
        if (typeof refreshStreamUnreadCounts === "function") {
          refreshStreamUnreadCounts();
        }
      });
      return;
    }

    if (typeof refreshStreamUnreadCounts === "function") {
      refreshStreamUnreadCounts();
    }
  };

  return (
    <div
      className={
        isSelected
          ? "channel-preview__wrapper__selected"
          : "channel-preview__wrapper"
      }
      onClick={handleSelectChannel}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleSelectChannel();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <div className="channel-preview__item">
        {isManagersChannel ? (
          <Avatar
            name={user.firstName.split(" ")[0] + " " + user.lastName}
            size="36"
            round
            textSizeRatio={1.7}
            color="#009eef"
            maxInitials={2}
          />
        ) : (
          <div className="channel-preview__icon">
            <PeopleIcon style={{ color: "white", fontSize: 20 }} />
          </div>
        )}
        <div className="channel-preview__content-wrapper">
          <div className="channel-preview__content-name">
            {displayTitle || channel?.data?.name || "Channel"}
          </div>
          {unreadCount > 0 && (
            <div
              className={cx(
                "channel-preview__content-unread",
                classes.unreadBadge,
                pulseUnread && classes.badgePulse
              )}
              aria-label={`${unreadCount} unread messages`}
            >
              {unreadCountDisplay}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
